import { useEffect, useState } from "react";

import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Toolbar,
	Typography,
} from "@mui/material";
import { ContentCopy, VpnKey } from "@mui/icons-material";

import { createApiKey, getApiKeys, revokeApiKey } from "../../../apis/AiSearch";

const EXPIRY_OPTIONS = [30, 90, 365];

const formatDate = (value) => new Date(value).toLocaleDateString();

function NewKey({ apiKey, onDismiss }) {
	const [copied, setCopied] = useState(false);
	const endpoint = `${(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/search/ai-search-query/`;

	const handleCopy = async () => {
		await navigator.clipboard.writeText(apiKey.key);
		setCopied(true);
	};

	return (
		<Alert severity="success" onClose={onDismiss} sx={{ mb: 3, "& .MuiAlert-message": { minWidth: 0 } }}>
			<Typography variant="subtitle2" gutterBottom>
				Copy the key for {apiKey.name} now. It won&apos;t be shown again.
			</Typography>
			<Stack direction="row" alignItems="center" gap={1} mb={1}>
				<Typography
					component="code"
					variant="body2"
					sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
				>
					{apiKey.key}
				</Typography>
				<Button
					size="small"
					startIcon={<ContentCopy />}
					onClick={handleCopy}
					sx={{ flexShrink: 0 }}
				>
					{copied ? "Copied" : "Copy"}
				</Button>
			</Stack>
			<Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
				curl -H &quot;X-API-Key: &lt;key&gt;&quot; &quot;{endpoint}?query=red+dress&quot;
			</Typography>
		</Alert>
	);
}

export default function ApiKeysPage() {
	const [keys, setKeys] = useState(null);
	const [form, setForm] = useState({ name: "", expiresInDays: 90 });
	const [newKey, setNewKey] = useState(null);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);
	const [revoking, setRevoking] = useState(null);

	const loadKeys = async () => {
		const res = await getApiKeys();
		if (res.success) {
			setKeys(res.keys);
		} else {
			setKeys([]);
			setError(res.message);
		}
	};

	useEffect(() => {
		loadKeys();
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setSaving(true);
		setError(null);
		const res = await createApiKey({ ...form, name: form.name.trim() });
		setSaving(false);

		if (res.success) {
			setNewKey(res.apiKey);
			setForm((prev) => ({ ...prev, name: "" }));
			loadKeys();
		} else {
			setError(res.message);
		}
	};

	const handleRevoke = async () => {
		const res = await revokeApiKey(revoking.id);
		setRevoking(null);

		if (res.success) {
			setError(null);
			loadKeys();
		} else {
			setError(res.message);
		}
	};

	return (
		<Grid container justifyContent={"center"}>
			<Grid item xs={12} md={10}>
				<Box sx={{ p: 5 }}>
					<Toolbar sx={{ px: 0 }}>
						<Typography variant="h4" noWrap>
							API Keys
						</Typography>
					</Toolbar>

					<Typography variant="body2" color="text.secondary" pb={3}>
						Keys let AI agents query AI Search. Create one key per agent, so any
						of them can be revoked without affecting the others.
					</Typography>

					{newKey && <NewKey apiKey={newKey} onDismiss={() => setNewKey(null)} />}
					{error && (
						<Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
							{error}
						</Alert>
					)}

					<Box component="form" onSubmit={handleSubmit} pb={4}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Agent name"
									placeholder="e.g. shopping-agent"
									inputProps={{ maxLength: 100 }}
									value={form.name}
									onChange={(event) =>
										setForm((prev) => ({ ...prev, name: event.target.value }))
									}
								/>
							</Grid>
							<Grid item xs={6} md={3}>
								<FormControl fullWidth>
									<InputLabel>Expires in</InputLabel>
									<Select
										label="Expires in"
										value={form.expiresInDays}
										onChange={(event) =>
											setForm((prev) => ({
												...prev,
												expiresInDays: event.target.value,
											}))
										}
									>
										{EXPIRY_OPTIONS.map((days) => (
											<MenuItem key={days} value={days}>
												{days} days
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={6} md={3}>
								<Button
									fullWidth
									type="submit"
									variant="contained"
									startIcon={<VpnKey />}
									disabled={saving || !form.name.trim()}
								>
									Generate key
								</Button>
							</Grid>
						</Grid>
					</Box>

					{keys === null ? (
						<Stack alignItems="center" py={8}>
							<CircularProgress />
						</Stack>
					) : keys.length === 0 ? (
						<Alert severity="info">No active API keys</Alert>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Name</TableCell>
										<TableCell>Key</TableCell>
										<TableCell>Permissions</TableCell>
										<TableCell>Created by</TableCell>
										<TableCell>Created</TableCell>
										<TableCell>Expires</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{keys.map((key) => (
										<TableRow key={key.id}>
											<TableCell>{key.name}</TableCell>
											<TableCell sx={{ fontFamily: "monospace" }}>
												{key.key_prefix}…
											</TableCell>
											<TableCell>
												{key.scopes.map((scope) => (
													<Chip key={scope} size="small" label={scope} />
												))}
											</TableCell>
											<TableCell>{key.created_by || "-"}</TableCell>
											<TableCell>{formatDate(key.created_at)}</TableCell>
											<TableCell>
												{key.expires_at ? formatDate(key.expires_at) : "Never"}
											</TableCell>
											<TableCell align="right">
												<Button
													size="small"
													color="error"
													onClick={() => setRevoking(key)}
												>
													Revoke
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					<Dialog open={Boolean(revoking)} onClose={() => setRevoking(null)}>
						<DialogTitle>Revoke {revoking?.name}?</DialogTitle>
						<DialogContent>
							<DialogContentText>
								Anything using this key will stop working straight away. This
								can&apos;t be undone.
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setRevoking(null)}>Cancel</Button>
							<Button color="error" onClick={handleRevoke}>
								Revoke
							</Button>
						</DialogActions>
					</Dialog>
				</Box>
			</Grid>
		</Grid>
	);
}
